import axios from "axios";
import { useCallback, useContext, useState } from "react";
import { GlobalStates } from "../GlobalStates";

import useGlobalData from './useGlobalData';

export const useStrapiObjectClass = (options, defaultValue = {}) => {
    const { token, path } = useContext(GlobalStates);
    const { name, path: url = path } = options;
    const { originalData, setOriginalData, isDataLoaded } = useGlobalData();
    const [data, setData] = useState(defaultValue);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const buildQuery = (filters = {}) => {
        const query = ['pagination[start]=0', 'pagination[limit]=-1']; // -1 means no limit
        
        // Handle filters
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                const encodedKey = encodeURIComponent(key);
                let encodedValue = encodeURIComponent(value);

                // Replace problematic characters directly
                encodedValue = encodedValue.replace(/%C4%AE/g, 'Į')
                                        .replace(/%C4%97/g, 'ė')
                                        .replace(/%20/g, ' ');
                
                query.push(`filters[${encodedKey}][$eq]=${encodedValue}`);
            }
        });
        return query.join('&');
    };

    const getAll = useCallback(async (filters = {}) => {
        const dataKey = name === 'atsakymai' ? 'atsakymai' : 
                       name === 'formos' ? 'tipai' : 
                       name === 'atsakymai-total' ? 'atsakymaiTotal' : null;

        if (dataKey && isDataLoaded && Object.keys(filters).length === 0 && originalData[dataKey]?.length > 0) {
            setData(originalData[dataKey]);
            setLoading(false);
            return originalData[dataKey];
        }

        setLoading(true);
        setError(null);

        const endpoint = typeof filters === 'string' 
            ? `${name}?${filters}` 
            : `${name}?${buildQuery(filters)}`;
        
        try {
            const response = await axios.get(url + endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            const responseData = response.data.data;

            if (dataKey && Object.keys(filters).length === 0) {
                setOriginalData({
                    [dataKey]: responseData
                });
            }

            setData(responseData);
            setLoading(false);
            return responseData;
        } catch (error) {
            setError(error);
            setLoading(false);
            setData([]);
            return [];
        }
    }, [name, url, token, isDataLoaded, originalData, setOriginalData]);

    const getSingle = useCallback(async (id, setter) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get(url + name + '/' + id, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            
            if (setter) {
                setter(response.data.data);
            }
            setLoading(false);
            return response.data.data;
        } catch (error) {
            setError(error);
            setLoading(false);
        }
    }, [name, token, url]);

    const update = useCallback(async (id, info, setter) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(url + name + '/' + id, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ data: info }),
            });
            
            const data = await response.json();
            
            if (setter) {
                setter(data.data);
            }
            
            setLoading(false);
            return data.data;
        } catch (error) {
            setError(error);
            setLoading(false);
        }
    }, [name, token, url]);

    const DELETE = useCallback(async (id, getAll) => {
        return fetch(url + name + '/' + id, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        })
            .then((response) => response.json())
            .then((data) => {
                if (getAll) getAll();
                return data;
            })
            .catch((error) => {
            });
    }, [name, token, url]);

    const create = useCallback(async (info) => {
        return fetch(url + name, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                data: info,
            }),
        })
            .then((response) => response.json())
            .catch((error) => {
            });
    }, [name, token, url]);

    return {
        data,
        isLoading,
        error,
        getAll,
        getSingle,
        update,
        DELETE,
        create,
        setData,
    };
};
